import { minBy, partition } from 'es-toolkit';

import { Profile, ProfileTrussSettings } from '@paragon/contract-library';
import { Direction2D, distanceEquals, distanceGreaterThan, distanceLessThan, Point2D } from '@paragon/geometry-library';
import { notNullish, Result, wrapErr, wrapOk } from '@paragon/utilities';

import { ProfileLineClass } from './ProfileLineClass';

function strictlyOrientWith(line: ProfileLineClass, direction: Direction2D) {
  if (line.segment.direction.equals(direction)) return wrapOk(line);
  if (line.segment.direction.reverse().equals(direction)) return wrapOk(line.reverse());
  return wrapErr('Expected profile line to be parallel to direction.');
}

/**
 * An immutable view of a profile (and its corresponding profile truss settings) with profile lines that are categorized
 * and oriented for ease of use and simpler logic. All the profiles lines are oriented "left-to-right" (with vertical
 * lines oriented so that they connect a left segment to a right segment).
 */
export class ProfileClass {
  readonly settingsData: ProfileTrussSettings;
  /**
   * The underlying profile data with its lines oriented and ordered from "left-to-right" (i.e., in the
   * order/orientation that a user can input them). This means that the top half of the profile (heels and top chords)
   * come first, followed by the bottom half (seat cuts and bottom chords). Each half starts with the origin and follows
   * the profile lines end-to-end until reaching the joint at the span of the profile.
   */
  readonly profileData: Profile;
  readonly span: number;

  /**
   * Oriented upward (to maintain the "left-to-right" orientation of the top half of the profile).
   */
  readonly leftHeel: ProfileLineClass | null;
  readonly topChords: readonly ProfileLineClass[];
  /**
   * Oriented downward (to maintain the "left-to-right" orientation of the top half of the profile).
   */
  readonly rightHeel: ProfileLineClass | null;

  readonly leftSeatCut: ProfileLineClass | null;
  readonly bottomChords: readonly ProfileLineClass[];
  readonly rightSeatCut: ProfileLineClass | null;

  readonly workingHalf: 'Top' | 'Bottom' | null;
  readonly isClosed: boolean;

  private constructor(
    settingsData: ProfileTrussSettings,
    profileData: Profile,
    span: number,
    leftHeel: ProfileLineClass | null,
    topChords: readonly ProfileLineClass[],
    rightHeel: ProfileLineClass | null,
    leftSeatCut: ProfileLineClass | null,
    bottomChords: readonly ProfileLineClass[],
    rightSeatCut: ProfileLineClass | null,
    workingHalf: 'Top' | 'Bottom' | null,
    isClosed: boolean,
  ) {
    this.settingsData = settingsData;
    this.profileData = profileData;
    this.span = span;

    this.leftHeel = leftHeel;
    this.topChords = topChords;
    this.rightHeel = rightHeel;

    this.leftSeatCut = leftSeatCut;
    this.bottomChords = bottomChords;
    this.rightSeatCut = rightSeatCut;

    this.workingHalf = workingHalf;
    this.isClosed = isClosed;
  }

  static create(profileTrussSettings: ProfileTrussSettings): Result<ProfileClass, string> {
    const { profile, span } = profileTrussSettings;
    const { profileJoints } = profile;

    if (
      profileJoints.some(
        (joint) => distanceLessThan(joint.location.x, 0) || distanceGreaterThan(joint.location.x, span),
      )
    ) {
      return wrapErr('Expected all joints to be within the span.');
    }

    const [lineSuccesses, lineFailures] = partition(
      profile.profileLines.map((line) => ProfileLineClass.fromProfileLineAndJoints(line, profileJoints)),
      (result) => result.type === 'Ok',
    );
    if (lineFailures.length > 0) return lineFailures[0];
    const lines = lineSuccesses.map((result) => result.ok);

    const unprocessedHeels = lines.filter((line) => line.profileLineType === 'Heel');
    const unprocessedTopChords = lines.filter((line) => line.profileLineType === 'TopChord');
    const unprocessedBottomChords = lines.filter((line) => line.profileLineType === 'BottomChord');
    const unprocessedSeatCuts = lines.filter((line) => line.profileLineType === 'SeatCut');

    const heelsResult = ProfileClass.processHeels(unprocessedHeels, span);
    if (heelsResult.type === 'Err') return heelsResult;
    const { leftHeel, rightHeel } = heelsResult.ok;

    const seatCutsResult = ProfileClass.processSeatCuts(unprocessedSeatCuts, span);
    if (seatCutsResult.type === 'Err') return seatCutsResult;
    const { leftSeatCut, rightSeatCut } = seatCutsResult.ok;

    const topChordsResult = ProfileClass.processChords(unprocessedTopChords);
    if (topChordsResult.type === 'Err') return topChordsResult;
    const topChords = topChordsResult.ok;

    const bottomChordsResult = ProfileClass.processChords(unprocessedBottomChords);
    if (bottomChordsResult.type === 'Err') return bottomChordsResult;
    const bottomChords = bottomChordsResult.ok;

    // NOTE: The left/right end of the profile might not start/end at Y=0 (e.g., if there's a step in the truss). We do
    // not currently support creating these kinds of profiles with our creation tools, but we should avoid throwing
    // errors for them.

    const firstTopChord = topChords.at(0);
    if (leftHeel) {
      if (firstTopChord && !firstTopChord.basePoint.equals(leftHeel.endPoint)) {
        return wrapErr('Unexpected start location for top chords.');
      }
    } else if (firstTopChord && !distanceEquals(firstTopChord.basePoint.x, 0)) {
      return wrapErr('Unexpected start location for top chords.');
    }

    const lastTopChord = topChords.at(-1);
    const isTopHalfComplete =
      rightHeel ||
      leftSeatCut ||
      bottomChords.length > 0 ||
      // If there is no heel or bottom chords yet, we require the last top chord to reach Y=0 since it's ambiguous
      // whether we're waiting for the user to enter the right heel or they want to move on to the bottom half.
      (lastTopChord && lastTopChord.endPoint.equals(new Point2D(span, 0)));

    if (rightHeel) {
      if (lastTopChord && !lastTopChord.endPoint.equals(rightHeel.basePoint)) {
        return wrapErr('Unexpected end location for top chords.');
      }
    } else if (isTopHalfComplete && !distanceEquals(lastTopChord!.endPoint.x, span)) {
      return wrapErr('Unexpected end location for top chords.');
    }

    if (!isTopHalfComplete && rightSeatCut)
      return wrapErr('Unexpected right seat line exists given that the top half is not complete.');

    const expectedBottomChordStart = leftSeatCut?.endPoint ?? leftHeel?.basePoint ?? firstTopChord?.basePoint;
    const firstBottomChord = bottomChords.at(0);
    if (firstBottomChord && !firstBottomChord.basePoint.equals(expectedBottomChordStart!))
      return wrapErr('Unexpected start location for bottom chords.');

    const expectedBottomChordEnd = rightSeatCut?.basePoint ?? rightHeel?.endPoint ?? lastTopChord?.endPoint;
    const lastBottomChord = bottomChords.at(-1);
    const isBottomHalfComplete = lastBottomChord && lastBottomChord.endPoint.equals(expectedBottomChordEnd!);

    if (!isBottomHalfComplete && rightSeatCut)
      return wrapErr('Unexpected right seat line exists given that the bottom half is not complete.');

    const sortedAndOrientedProfileData = {
      ...profile,
      profileLines: [leftHeel, ...topChords, rightHeel, leftSeatCut, ...bottomChords, rightSeatCut]
        .filter(notNullish)
        .map((line) => line.lineData),
    };

    const settingsData: ProfileTrussSettings = { ...profileTrussSettings, profile: sortedAndOrientedProfileData };

    const workingHalf = isBottomHalfComplete ? null : isTopHalfComplete ? 'Bottom' : 'Top';
    const isClosed = workingHalf === null;

    const profileClass = new ProfileClass(
      settingsData,
      sortedAndOrientedProfileData,
      span,

      leftHeel,
      topChords,
      rightHeel,

      leftSeatCut,
      bottomChords,
      rightSeatCut,

      workingHalf,
      isClosed,
    );
    return wrapOk(profileClass);
  }

  private static processHeels(heels: readonly ProfileLineClass[], span: number) {
    if (heels.length > 2) return wrapErr('Expected two heels at most.');

    const leftHeelCandidates = heels.filter(
      (line) => distanceEquals(line.basePoint.x, 0) && distanceEquals(line.endPoint.x, 0),
    );
    if (leftHeelCandidates.length > 1) return wrapErr('Expected one left heel at most.');
    const unorientedLeftHeel = leftHeelCandidates.at(0) ?? null;
    const leftHeelResult = unorientedLeftHeel != null ? strictlyOrientWith(unorientedLeftHeel, Direction2D.up) : null;
    if (leftHeelResult?.type === 'Err') return leftHeelResult;
    const leftHeel = leftHeelResult?.ok ?? null;
    if (leftHeel && !distanceEquals(leftHeel.basePoint.x, 0)) return wrapErr('Expected left heel to start at 0.');

    const rightHeelCandidates = heels.filter(
      (line) => distanceEquals(line.basePoint.x, span) && distanceEquals(line.endPoint.x, span),
    );
    if (rightHeelCandidates.length > 1) return wrapErr('Expected one right heel at most.');
    const unorientedRightHeel = rightHeelCandidates.at(0) ?? null;
    const rightHeelResult =
      unorientedRightHeel != null ? strictlyOrientWith(unorientedRightHeel, Direction2D.down) : null;
    if (rightHeelResult?.type === 'Err') return rightHeelResult;
    const rightHeel = rightHeelResult?.ok ?? null;
    if (rightHeel && !distanceEquals(rightHeel.endPoint.x, span)) return wrapErr('Expected right heel to end at span.');

    if (heels.some((line) => line.guid !== leftHeel?.guid && line.guid !== rightHeel?.guid))
      return wrapErr('Invalid heel exists.');

    return wrapOk({ leftHeel, rightHeel });
  }

  private static processSeatCuts(seatCuts: readonly ProfileLineClass[], span: number) {
    if (seatCuts.length > 2) return wrapErr('Expected two seat cuts at most.');

    const leftSeatCutCandidates = seatCuts.filter(
      (line) => distanceEquals(line.basePoint.x, 0) || distanceEquals(line.endPoint.x, 0),
    );
    if (leftSeatCutCandidates.length > 1) return wrapErr('Expected one left seat cut at most.');
    const unorientedLeftSeatCut = leftSeatCutCandidates.at(0) ?? null;
    const leftSeatCutResult =
      unorientedLeftSeatCut != null ? strictlyOrientWith(unorientedLeftSeatCut, Direction2D.right) : null;
    if (leftSeatCutResult?.type === 'Err') return leftSeatCutResult;
    const leftSeatCut = leftSeatCutResult?.ok ?? null;
    if (leftSeatCut && !distanceEquals(leftSeatCut.basePoint.x, 0))
      return wrapErr('Expected left seat cut to start at 0.');

    const rightSeatCutCandidates = seatCuts.filter(
      (line) => distanceEquals(line.basePoint.x, span) || distanceEquals(line.endPoint.x, span),
    );
    if (rightSeatCutCandidates.length > 1) return wrapErr('Expected one right seat cut at most.');
    const unorientedRightSeatCut = rightSeatCutCandidates.at(0) ?? null;
    const rightSeatCutResult =
      unorientedRightSeatCut != null ? strictlyOrientWith(unorientedRightSeatCut, Direction2D.right) : null;
    if (rightSeatCutResult?.type === 'Err') return rightSeatCutResult;
    const rightSeatCut = rightSeatCutResult?.ok ?? null;
    if (rightSeatCut && !distanceEquals(rightSeatCut.endPoint.x, span))
      return wrapErr('Expected right seat cut to end at span.');

    if (seatCuts.some((line) => line.guid !== leftSeatCut?.guid && line.guid !== rightSeatCut?.guid))
      return wrapErr('Invalid seat cut exists.');

    return wrapOk({ leftSeatCut, rightSeatCut });
  }

  private static processChords(chords: readonly ProfileLineClass[]) {
    let leftMostChord = minBy(chords, (chord) => Math.min(chord.basePoint.x, chord.endPoint.x));

    if (!leftMostChord) return wrapOk([]);

    let remainingChords = chords.filter((line) => line !== leftMostChord);

    if (leftMostChord.segment.basePoint.x > leftMostChord.segment.endPoint.x) {
      leftMostChord = leftMostChord.reverse();
    }

    const sortedChords = [leftMostChord];
    let currentPoint = leftMostChord.endPoint;

    while (remainingChords.length !== 0) {
      const current = currentPoint;
      let nextChord = remainingChords.find((line) => line.basePoint.equals(current) || line.endPoint.equals(current));
      if (!nextChord) return wrapErr('Some chords were not processed.');

      remainingChords = remainingChords.filter((line) => line !== nextChord);
      if (nextChord.endPoint.equals(currentPoint)) {
        nextChord = nextChord.reverse();
      }

      sortedChords.push(nextChord);
      currentPoint = nextChord.endPoint;
    }

    return wrapOk(sortedChords);
  }

  /**
   * Gets the profile lines that should be removed when the user clicks "Back Up." This should remove the most recent
   * profile lines that have been entered and include at most one chord and any heels (if a top chord is being removed)
   * or seat cuts (if a bottom chord is being removed) that are adjacent to that chord (either before or after it).
   */
  getLastProfileLines() {
    const getLastLinesOnHalf = (
      chordProfileLines: ReadonlyArray<ProfileLineClass>,
      leftSideLine: ProfileLineClass | null,
      rightSideLine: ProfileLineClass | null,
    ): readonly ProfileLineClass[] | undefined => {
      if (chordProfileLines.length === 1) {
        return [leftSideLine, chordProfileLines[0], rightSideLine].filter(notNullish);
      }

      const rightMostChord = chordProfileLines.at(-1);
      if (rightMostChord) {
        return [rightMostChord, rightSideLine].filter(notNullish);
      }

      console.assert(rightSideLine == null, "There shouldn't be a right side line if there are no chords.");
      if (leftSideLine) return [leftSideLine];
      return undefined;
    };

    const lastBottomLines = getLastLinesOnHalf(this.bottomChords, this.leftSeatCut, this.rightSeatCut);
    if (lastBottomLines) return lastBottomLines;
    const lastTopLines = getLastLinesOnHalf(this.topChords, this.leftHeel, this.rightHeel);
    if (lastTopLines) return lastTopLines;

    return [];
  }

  getLastProfileLine() {
    return this.getLastProfileLines().at(-1) ?? null;
  }

  /**
   * The heel height is defined in the heel settings, but the profile geometry is the authoritative source for the heel
   * height if the profile includes a heel line on this side of the truss, or if the profile has already moved past the
   * stage where a heel can be added on this side of the truss.
   */
  getLeftHeelHeightSource(): 'HeelSettings' | 'ProfileGeometry' {
    return this.leftHeel != null || this.topChords.length > 0 ? 'ProfileGeometry' : 'HeelSettings';
  }

  /**
   * The heel height is defined in the heel settings, but the profile geometry is the authoritative source for the heel
   * height if the profile includes a heel line on this side of the truss, or if the profile has already moved past the
   * stage where a heel can be added on this side of the truss.
   */
  getRightHeelHeightSource(): 'HeelSettings' | 'ProfileGeometry' {
    return this.workingHalf === 'Top' ? 'HeelSettings' : 'ProfileGeometry';
  }
}
